import { createContext, useContext } from 'react';
import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes, HTMLAttributes } from 'react';
import { cx } from './cx.ts';

/**
 * Field Notes table set. Rules (DESIGN.md §6):
 *   - No zebra striping — hairline row dividers + hover tone carry the eye.
 *   - Column headers render at `label` size in `ink-low`, sentence case.
 *   - Numeric columns are right-aligned mono (`num` prop) — money, counts,
 *     rates are always tabular.
 *   - Row actions stay visible (or fold into a menu); hover-opacity
 *     reveals are banned.
 *
 * Responsive: below `md` a `responsive` table stops being a grid and
 * each row becomes a stacked card — the header hides and every `Td`
 * shows its `label` next to (or above) its value, so wide tables read
 * top-to-bottom on phones with NO horizontal scroll. Desktop (`md`+) is
 * unchanged. Give each `Td` a `label` matching its `Th`; mark the
 * primary cell `heading` (card title) and control/long cells `stack`
 * (label above a full-width value). Opt a table out with
 * `responsive={false}`.
 */
const ResponsiveContext = createContext<boolean>(true);

interface TableProps {
  readonly children: ReactNode;
  readonly stickyHeader?: boolean;
  readonly responsive?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function Table({
  children,
  stickyHeader = false,
  responsive = true,
  className,
  testId,
}: TableProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      className={cx(
        'overflow-x-auto rounded-lg border border-line bg-card',
        // On phones the rows are self-contained cards, so drop the wrapper
        // chrome (border/fill/radius) to avoid a frame-inside-frame look.
        responsive && 'max-md:rounded-none max-md:border-0 max-md:bg-transparent',
        stickyHeader && 'max-h-[70vh] overflow-y-auto',
        className,
      )}
    >
      <ResponsiveContext.Provider value={responsive}>
        <table
          className={cx(
            'w-full border-collapse text-body',
            responsive && 'max-md:block',
            stickyHeader && '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10',
          )}
        >
          {children}
        </table>
      </ResponsiveContext.Provider>
    </div>
  );
}

export function THead({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  const responsive = useContext(ResponsiveContext);
  return (
    <thead className={cx('bg-card', responsive && 'max-md:hidden', className)} {...rest}>
      {children}
    </thead>
  );
}

export function TBody({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  const responsive = useContext(ResponsiveContext);
  return (
    <tbody className={cx(responsive && 'max-md:block', className)} {...rest}>
      {children}
    </tbody>
  );
}

interface TrProps extends HTMLAttributes<HTMLTableRowElement> {
  readonly interactive?: boolean;
}

export function Tr({ interactive = true, className, children, ...rest }: TrProps): JSX.Element {
  const responsive = useContext(ResponsiveContext);
  return (
    <tr
      className={cx(
        // Hover tint only where a pointer exists (desktop grid); a phone
        // card already carries its own border + fill.
        interactive && 'transition-colors duration-150 md:hover:bg-well/60',
        '[&:last-child>td]:border-b-0',
        responsive &&
          'max-md:mb-2 max-md:block max-md:rounded-lg max-md:border max-md:border-line max-md:bg-card max-md:p-3 max-md:last:mb-0',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  readonly num?: boolean;
}

export function Th({ num = false, className, children, ...rest }: ThProps): JSX.Element {
  return (
    <th
      scope="col"
      className={cx(
        'border-b border-line px-4 py-3 text-label text-ink-low',
        num ? 'text-right font-mono' : 'text-left',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  readonly num?: boolean;
  readonly mono?: boolean;
  readonly emphasis?: boolean;
  /**
   * Mobile card label for this cell (mirrors the column's `Th`). Rendered
   * only below `md`; ignored on desktop and when `heading` is set.
   */
  readonly label?: ReactNode;
  /**
   * Primary cell — becomes the card title below `md`: full-width, bold, a
   * hairline underline, and NO label. Use for the row's identifying field
   * (email, name, id, timestamp).
   */
  readonly heading?: boolean;
  /**
   * Stack the label ABOVE a full-width value below `md` (instead of the
   * default label:value side-by-side). Use for controls (`Select`) and
   * long/wrapping content that should own the card width.
   */
  readonly stack?: boolean;
}

export function Td({
  num = false,
  mono = false,
  emphasis = false,
  label,
  heading = false,
  stack = false,
  className,
  children,
  ...rest
}: TdProps): JSX.Element {
  const responsive = useContext(ResponsiveContext);
  const showLabel = responsive && !heading && label !== undefined && label !== null;
  return (
    <td
      className={cx(
        'border-b border-line-soft px-4 py-3',
        num && 'text-right font-mono tabular-nums',
        !num && mono && 'font-mono',
        emphasis ? 'font-medium text-ink' : 'text-ink-mid',
        responsive &&
          (heading
            ? 'max-md:mb-2 max-md:block max-md:border-b max-md:border-line-soft max-md:px-0 max-md:pb-2 max-md:pt-0 max-md:text-left max-md:font-medium max-md:text-ink'
            : stack
              ? 'max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-1 max-md:border-0 max-md:px-0 max-md:py-1 max-md:text-left'
              : 'max-md:flex max-md:items-baseline max-md:justify-between max-md:gap-4 max-md:border-0 max-md:px-0 max-md:py-1 max-md:text-left'),
        className,
      )}
      {...rest}
    >
      {showLabel ? (
        <span className="hidden text-label text-ink-low max-md:block max-md:shrink-0">{label}</span>
      ) : null}
      {children}
    </td>
  );
}
